import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from '@fluentui/react-components';
import {
  CalendarLtr20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
} from '@fluentui/react-icons';
import { DayPicker, useDayPicker } from 'react-day-picker';

interface DatePickerProps {
  value: Date | null;
  onSelectDate: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  /** Override the visible label format. Default: "Mon DD, YYYY". */
  formatDate?: (date: Date) => string;
  /** Visual size; matches the Fluent Input sizing convention. */
  size?: 'small' | 'medium' | 'large';
  /**
   * Hide the trailing calendar glyph. Use where horizontal space is tight and
   * the field is otherwise obviously a date control (labeled + click-to-open),
   * e.g. the two pickers sharing one narrow phase-card row.
   */
  hideIcon?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Days between `from` and `to` get a blue pill outline that visually spans
   * the range — same look as Booqable's rental-period picker. Both ends must
   * be set for the pill to render; the caller doesn't need to keep the dates
   * in chronological order — the component normalizes.
   */
  highlightedRange?: { from: Date | null; to: Date | null };
}

const SIZE_HEIGHTS: Record<NonNullable<DatePickerProps['size']>, string> = {
  small: '24px',
  medium: '32px',
  large: '40px',
};

const defaultFormat = (d: Date) =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const useStyles = makeStyles({
  // ── Trigger (input-styled button) ───────────────────────────────────────
  trigger: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
    ...shorthands.padding('0', '10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    fontFamily: 'inherit',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
    },
    '&:focus-visible': {
      outlineStyle: 'none',
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
    '&:disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
  },
  // Truncate the date label rather than wrapping/overflowing when the trigger
  // is squeezed (e.g. two pickers sharing one narrow row).
  triggerLabel: {
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  placeholder: {
    color: tokens.colorNeutralForeground3,
  },
  triggerIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },

  // ── Popover surface ─────────────────────────────────────────────────────
  surface: {
    ...shorthands.padding('12px'),
    minWidth: 'unset',
    // The full 7×52px grid is wider than a phone — cap the surface to the
    // viewport and tighten padding so the calendar never spills off-screen.
    '@media (max-width: 480px)': {
      ...shorthands.padding('8px'),
      maxWidth: 'calc(100vw - 16px)',
      boxSizing: 'border-box',
    },
  },

  // ── Calendar shell ──────────────────────────────────────────────────────
  rdpRoot: {
    fontFamily: 'inherit',
    color: tokens.colorNeutralForeground1,
    // Booqable-style grid sizing — generous cells so the green availability
    // capsule has breathing room and the selection ring can sit outside it.
    '--rdp-day-height': '52px',
    '--rdp-day-width': '52px',
    '--rdp-day_button-height': '52px',
    '--rdp-day_button-width': '52px',
    '--rdp-accent-color': tokens.colorBrandBackground,
    '--rdp-accent-background-color': tokens.colorBrandBackground2,
    // Shrink the cell grid on phones (7×52px overflows a ~360px viewport).
    '@media (max-width: 480px)': {
      '--rdp-day-height': '42px',
      '--rdp-day-width': '42px',
      '--rdp-day_button-height': '42px',
      '--rdp-day_button-width': '42px',
    },
  },
  // Single-row header containing [prev] [month/year] [next]. The default RDP
  // Nav is hidden via `hideNavigation` so this is the only navigation surface.
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
    ...shorthands.padding('0', '4px', '8px'),
  },
  headerNavButton: {
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('4px'),
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '&:disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  headerCaption: {
    fontSize: '14px',
    fontWeight: 600,
    // Centered between the two chevrons.
    flexGrow: 1,
    textAlign: 'center',
  },
  rdpWeekday: {
    width: '52px',
    height: '36px',
    lineHeight: '36px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    '@media (max-width: 480px)': {
      width: '42px',
    },
  },
  // Collapse the day grid table so adjacent <td> cells touch — otherwise
  // the default 2px border-spacing breaks the range-pill rails into dashes.
  rdpGrid: {
    borderCollapse: 'collapse',
    borderSpacing: 0,
  },
  rdpDay: {
    ...shorthands.padding('0'),
  },

  // ── Day cell — nested wrapper/availability/inner/date to mirror Booqable
  //    structure, leaving an availability layer ready for inventory data.
  //    Each day renders as a green-filled "available" circle by default; the
  //    selected day overlays a blue ring on top.
  dayButton: {
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('2px'),
    width: '52px',
    height: '52px',
    '@media (max-width: 480px)': {
      width: '42px',
      height: '42px',
    },
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '15px',
    color: tokens.colorNeutralForeground1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    // Anchor for the range-pill pseudo-element so its borders draw at the
    // cell's outer edges. Adjacent cells' borders then visually merge into
    // one continuous pill across rows.
    position: 'relative',
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  // Selection ring — a blue stroke around the cell with full rounding.
  // Sits outside the green availability circle for a Booqable-style outline.
  // Positioned so it stacks above the range band's absolute pseudo-element;
  // otherwise the band would cover the green circle + day number.
  dayWrapper: {
    width: '38px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.borderRadius('50%'),
    ...shorthands.border('2px', 'solid', 'transparent'),
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 1,
  },
  // Green "available" capsule. Sized smaller than the band's vertical
  // extent so a strip of the range fill is visible above and below it.
  // Becomes desaturated/red when an "unavailable" modifier is added
  // (future, when inventory flows in).
  dayAvailability: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.borderRadius('50%'),
    // Booqable's available-day green. Hardcoded — Fluent's palette has
    // no green token close enough to this saturation.
    backgroundColor: '#9CD89A',
  },
  dayInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  dayDate: {
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
    fontWeight: 600,
    color: '#1A1A1A',
  },
  // State modifiers
  dayToday: {
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    fontWeight: 700,
  },
  daySelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  dayDisabled: {
    opacity: 0.35,
  },

  // ── Range band — translucent brand-tinted fill drawn via a single
  //    pseudo-element on each in-range cell. Sits BEHIND the green
  //    availability circle (DOM order: ::before precedes the wrapper div,
  //    so the wrapper paints on top). Adjacent in-range cells' bands meet
  //    at the cell edge and merge into a single continuous fill across
  //    rows. The Booqable-style endpoint emphasis comes from the existing
  //    `daySelected` blue ring on the wrapper.
  dayInRange: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '4px',
      bottom: '4px',
      left: 0,
      right: 0,
      pointerEvents: 'none',
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  // Pickup end — band starts with a rounded left cap inset from the cell
  // edge so it hugs around the green circle.
  dayRangeStart: {
    '&::before': {
      left: '4px',
      borderTopLeftRadius: '22px',
      borderBottomLeftRadius: '22px',
    },
  },
  // Return end — same idea on the right side.
  dayRangeEnd: {
    '&::before': {
      right: '4px',
      borderTopRightRadius: '22px',
      borderBottomRightRadius: '22px',
    },
  },
});

// Day-precision comparison helpers — normalize to local midnight so range
// membership doesn't depend on the time component the caller passed in.
const startOfDay = (d: Date): number => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onSelectDate,
  minDate,
  maxDate,
  placeholder = 'Select date',
  formatDate = defaultFormat,
  size = 'medium',
  hideIcon = false,
  disabled = false,
  ariaLabel,
  className,
  style,
  highlightedRange,
}) => {
  // Normalize the range to chronological order + day-precision timestamps
  // so the DayButton callback below can do simple integer comparisons.
  const rangeBounds = React.useMemo(() => {
    if (!highlightedRange?.from || !highlightedRange?.to) return null;
    const a = startOfDay(highlightedRange.from);
    const b = startOfDay(highlightedRange.to);
    return a <= b ? { start: a, end: b } : { start: b, end: a };
  }, [highlightedRange?.from, highlightedRange?.to]);
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  // RDP needs an initial month to display when value is null — default to
  // minDate (if set) or today, so the calendar opens somewhere meaningful.
  const [month, setMonth] = useState<Date>(value ?? minDate ?? new Date());

  // Reset the displayed month each time the popover opens. Without this,
  // if a user navigated forward several months and closed the picker without
  // selecting, reopening would still show that stale month — surprising
  // when the value (or today) is somewhere else entirely.
  React.useEffect(() => {
    if (open) {
      setMonth(value ?? minDate ?? new Date());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (d: Date | undefined) => {
    // RDP fires `undefined` when the user re-clicks the currently-selected
    // day (its built-in toggle-off behavior). For rental dates that's almost
    // always an accidental clear — treat it as "confirm the existing value"
    // and just close the popover instead.
    if (d === undefined) {
      setOpen(false);
      return;
    }
    onSelectDate(d);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(_, data) => setOpen(data.open)}
      positioning="below-start"
      withArrow={false}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={`${styles.trigger} ${className ?? ''}`}
          style={{ height: SIZE_HEIGHTS[size], ...style }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={`${styles.triggerLabel} ${value ? '' : styles.placeholder}`}>
            {value ? formatDate(value) : placeholder}
          </span>
          {!hideIcon && <CalendarLtr20Regular className={styles.triggerIcon} />}
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <DayPicker
          mode="single"
          selected={value ?? undefined}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          startMonth={minDate}
          endMonth={maxDate}
          disabled={[
            ...(minDate ? [{ before: minDate }] : []),
            ...(maxDate ? [{ after: maxDate }] : []),
          ]}
          // Outside days suppressed — leading/trailing cells render empty so
          // only the visible month's dates show, matching the Booqable look.
          // Default Nav renders prev/next above the caption — we replace it
          // with a single-row header via the MonthCaption slot below.
          hideNavigation
          className={styles.rdpRoot}
          classNames={{
            weekday: styles.rdpWeekday,
            month_grid: styles.rdpGrid,
            day: styles.rdpDay,
          }}
          components={{
            MonthCaption: ({ calendarMonth }: { calendarMonth: { date: Date } }) => {
              const { goToMonth, nextMonth, previousMonth } = useDayPicker();
              const label = calendarMonth.date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              });
              return (
                <div className={styles.header}>
                  <button
                    type="button"
                    className={styles.headerNavButton}
                    disabled={!previousMonth}
                    onClick={() => previousMonth && goToMonth(previousMonth)}
                    aria-label="Previous month"
                  >
                    <ChevronLeft20Regular />
                  </button>
                  <span className={styles.headerCaption}>{label}</span>
                  <button
                    type="button"
                    className={styles.headerNavButton}
                    disabled={!nextMonth}
                    onClick={() => nextMonth && goToMonth(nextMonth)}
                    aria-label="Next month"
                  >
                    <ChevronRight20Regular />
                  </button>
                </div>
              );
            },
            DayButton: ({ day, modifiers, ...buttonProps }) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const cellDate = new Date(day.date);
              cellDate.setHours(0, 0, 0, 0);
              const cellTs = cellDate.getTime();
              const isToday = cellTs === today.getTime();
              const isSelected = !!modifiers.selected;
              const isDisabled = !!modifiers.disabled;

              const isInRange = !!rangeBounds && cellTs >= rangeBounds.start && cellTs <= rangeBounds.end;
              const isRangeStart = !!rangeBounds && cellTs === rangeBounds.start;
              const isRangeEnd = !!rangeBounds && cellTs === rangeBounds.end;

              // Endpoint emphasis: blue ring on the selected day's wrapper,
              // painted on top of the translucent band so pickup/return stand
              // out from the middle-range days.
              const wrapperClasses = [
                styles.dayWrapper,
                isSelected ? styles.daySelected : '',
              ]
                .filter(Boolean)
                .join(' ');

              const dayButtonClasses = [
                styles.dayButton,
                isInRange ? styles.dayInRange : '',
                isRangeStart ? styles.dayRangeStart : '',
                isRangeEnd ? styles.dayRangeEnd : '',
              ]
                .filter(Boolean)
                .join(' ');

              const availabilityClasses = [
                styles.dayAvailability,
                'available',
                isDisabled ? styles.dayDisabled : '',
              ]
                .filter(Boolean)
                .join(' ');

              const dateClasses = [
                styles.dayDate,
                isToday ? styles.dayToday : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  {...buttonProps}
                  className={dayButtonClasses}
                  // Booqable's nested structure: wrapper (selection ring) >
                  // availability (green capsule) > inner > date. The
                  // availability layer is the per-day inventory hook —
                  // future work flips a modifier class here when stock is
                  // depleted for that date.
                >
                  <div className={wrapperClasses}>
                    <div className={availabilityClasses}>
                      <div className={styles.dayInner}>
                        <span className={dateClasses}>{day.date.getDate()}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            },
          }}
        />
      </PopoverSurface>
    </Popover>
  );
};

export default DatePicker;
